import { useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Sheet } from "@/components/ui/sheet";
import { toast } from "@/components/ui/sonner";
import { Switch } from "@/components/ui/switch";
import { SelectionField } from "@/features/expert/components/registration/steps/controls";
import { THEMATIC_KEYS, IssueIcon, isCustomThematic, thematicClasses } from "@/lib/thematic";
import { cn } from "@/lib/utils/cn";

import { useCatalogueList, useCompanySizes } from "../api";
import { FONT_AWESOME_GALLERY } from "../constants";
import type { CatalogueItem, CatalogueKind, CatalogueWrite } from "../types";
import { ColourPicker } from "./ColourPicker";
import { IconPicker } from "./IconPicker";
import { ImpactDialog } from "./ImpactDialog";
import { useSaveFlow } from "./useSaveFlow";

type Values = Record<string, string | boolean | string[]>;

/** The fields each kind's form exposes (``content_forms.py``), in form order. */
const FIELDS: Record<CatalogueKind, string[]> = {
  thematic: [
    "name_fr",
    "name_en",
    "css_class",
    "colour",
    "experts_enabled",
    "tools_enabled",
    "financial_aids_enabled",
  ],
  issue: ["name_fr", "name_en", "thematic", "icon", "characteristics"],
  goal: ["name_fr", "name_en", "issue"],
  action: ["name_fr", "name_en", "goal", "sectors", "characteristics", "company_sizes"],
  sector: [
    "name_fr",
    "name_en",
    "category",
    "unavoidable_issues",
    "characteristics",
    "actions",
  ],
  sector_category: ["name_fr", "name_en"],
  characteristic: [
    "name_fr",
    "name_en",
    "group",
    "prerequisite",
    "sectors",
    "issues",
    "actions",
  ],
  characteristic_group: ["name_fr", "name_en"],
};

const LIST_FIELDS = new Set([
  "sectors",
  "characteristics",
  "company_sizes",
  "unavoidable_issues",
  "issues",
  "actions",
]);
/** The « Personnalisée » colour choice, stored as a token plus ``colour``. */
const CUSTOM = "custom";
const BOOL_FIELDS = new Set(["experts_enabled", "tools_enabled", "financial_aids_enabled"]);
const PARENT_FIELD: Partial<Record<CatalogueKind, string>> = {
  issue: "thematic",
  goal: "issue",
  action: "goal",
  sector: "category",
  characteristic: "group",
};

function initialValues(
  kind: CatalogueKind,
  item: CatalogueItem | null,
  defaults: Values,
): Values {
  const values: Values = {};
  for (const field of FIELDS[kind]) {
    if (LIST_FIELDS.has(field)) {
      values[field] = (item?.[field as keyof CatalogueItem] as string[] | undefined) ?? [];
    } else if (BOOL_FIELDS.has(field)) {
      values[field] = Boolean(item?.[field as keyof CatalogueItem]);
    } else if (field === PARENT_FIELD[kind]) {
      values[field] = item?.parent_id ?? "";
    } else if (field === "prerequisite") {
      values[field] = item?.prerequisite ?? "";
    } else if (field === "css_class" && isCustomThematic(item?.css_class)) {
      // The stored token reads back as the choice it came from.
      values[field] = CUSTOM;
    } else {
      values[field] = (item?.[field as keyof CatalogueItem] as string | undefined) ?? "";
    }
  }
  return { ...values, ...defaults };
}

/** The write body: every field, empty single choices sent as null. */
function toBody(kind: CatalogueKind, values: Values): CatalogueWrite {
  const body: Record<string, unknown> = {};
  for (const field of FIELDS[kind]) {
    const value = values[field];
    body[field] =
      (field === "group" || field === "prerequisite") && value === "" ? null : value;
  }
  return body as CatalogueWrite;
}

/** The issue icon as the questionnaire renders it, updated as the class is typed. */
function IconPreview({ icon }: { icon: string }) {
  return (
    <span className="flex size-9 shrink-0 items-center justify-center rounded-md border border-input bg-muted">
      <IssueIcon icon={icon} aria-hidden className="size-4" />
    </span>
  );
}

function Field({
  id,
  label,
  error,
  children,
  hint,
  required = false,
}: {
  id?: string;
  label: string;
  error?: string[];
  children: ReactNode;
  hint?: ReactNode;
  required?: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="block">
        {required && <span className="mr-0.5 text-primary">*</span>}
        {label}
      </Label>
      {children}
      {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
      {error?.map((message) => (
        <p key={message} className="text-sm text-destructive-ink">
          {message}
        </p>
      ))}
    </div>
  );
}

/**
 * Create or edit one row of a kind, with its form's fields.
 *
 * Pickers draw on the catalogue lists the page already reads, so an option is
 * exactly a row the server will accept. Validation is the server's (the same
 * form as the Django page); its messages land under their fields.
 */
export function ItemSheet({
  kind,
  item,
  defaults = {},
  onClose,
}: {
  kind: CatalogueKind;
  item: CatalogueItem | null;
  /** Preset values for a new row — the parent it is added under. */
  defaults?: Values;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const fields = FIELDS[kind];
  const [values, setValues] = useState<Values>(() =>
    initialValues(kind, item, defaults),
  );
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [pickingIcon, setPickingIcon] = useState(false);
  const flow = useSaveFlow(kind, (saved) => {
    toast.success(t("catalogue.saved", { name: saved.name }));
    onClose();
  });

  const needs = (field: string) => fields.includes(field);
  const thematics = useCatalogueList("thematic", needs("thematic") || needs("unavoidable_issues") || needs("issues"));
  const issues = useCatalogueList(
    "issue",
    needs("issue") || needs("unavoidable_issues") || needs("issues") || needs("goal") || needs("actions"),
  );
  const goals = useCatalogueList("goal", needs("goal") || needs("actions"));
  const actions = useCatalogueList("action", needs("actions"));
  const sectors = useCatalogueList("sector", needs("sectors"));
  const categories = useCatalogueList("sector_category", needs("category") || needs("sectors"));
  const characteristics = useCatalogueList(
    "characteristic",
    needs("characteristics") || needs("prerequisite"),
  );
  const groups = useCatalogueList("characteristic_group", needs("group") || needs("characteristics"));
  const sizes = useCompanySizes();

  const thematicRows = thematics.data;
  const issueRows = issues.data;
  const goalRows = goals.data;
  const categoryRows = categories.data;
  const groupRows = groups.data;
  const names = useMemo(() => {
    const map = new Map<string, string>();
    for (const list of [thematicRows, issueRows, goalRows, categoryRows, groupRows]) {
      for (const row of list ?? []) map.set(row.id, row.name);
    }
    return map;
  }, [thematicRows, issueRows, goalRows, categoryRows, groupRows]);

  const set = (field: string, value: string | boolean | string[]) =>
    setValues((current) => ({ ...current, [field]: value }));

  const grouped = (
    rows: CatalogueItem[] | undefined,
    parentName: (row: CatalogueItem) => string,
    label: (row: CatalogueItem) => string = (row) => row.name,
  ) => {
    const byHeading = new Map<string, { value: string; label: string }[]>();
    for (const row of rows ?? []) {
      const heading = parentName(row);
      const bucket = byHeading.get(heading) ?? [];
      bucket.push({ value: row.id, label: label(row) });
      byHeading.set(heading, bucket);
    }
    const groupsOut = [...byHeading.entries()].map(([heading, options]) => ({
      heading,
      options,
    }));
    return {
      options: groupsOut.flatMap((group) => group.options),
      groups: groupsOut,
    };
  };

  const issueOptions = grouped(issues.data, (row) => row.crumb);
  const characteristicOptions = grouped(
    characteristics.data,
    (row) => row.crumb || t("catalogue.noGroup"),
  );
  const sectorOptions = grouped(sectors.data, (row) => row.crumb || t("catalogue.noCategory"));
  const actionOptions = grouped(
    actions.data,
    (row) => {
      const goal = (goals.data ?? []).find((g) => g.id === row.parent_id);
      return goal ? `${names.get(goal.parent_id ?? "") ?? ""} › ${goal.name}` : row.crumb;
    },
  );
  const sizeOptions = (sizes.data ?? []).map((size) => ({
    value: size.id,
    label: size.name,
  }));

  const submit = async () => {
    setFailure(null);
    const outcome = await flow.submit(item?.id ?? null, toBody(kind, values));
    if (outcome.status === "invalid") setErrors(outcome.errors);
    else if (outcome.status === "failed") setFailure(outcome.message ?? t("catalogue.error"));
    else setErrors({});
  };

  const confirm = async () => {
    const outcome = await flow.confirm();
    if (outcome.status === "invalid") setErrors(outcome.errors);
    else if (outcome.status === "failed") setFailure(outcome.message ?? t("catalogue.error"));
  };

  const title = item
    ? t("catalogue.editTitle", { name: item.name })
    : t(`catalogue.addTitle.${kind}`);

  const single = (
    field: string,
    options: { id: string; name: string }[],
    allowEmpty = false,
  ) => (
    <Field
      id={`catalogue-${field}`}
      label={t(`catalogue.fields.${field}`)}
      error={errors[field]}
      required={!allowEmpty}
    >
      <SearchableSelect
        id={`catalogue-${field}`}
        value={values[field] as string}
        onChange={(next) => set(field, next)}
        options={options}
        placeholder={allowEmpty ? t("catalogue.none") : t("expert.register.select")}
        searchPlaceholder={t("catalogue.searchPlaceholder")}
        emptyLabel={t("catalogue.noResults")}
      />
    </Field>
  );

  const many = (
    field: string,
    labelKey: string,
    source: { options: { value: string; label: string }[]; groups?: { heading: string; options: { value: string; label: string }[] }[] },
    hint?: string,
  ) => (
    <SelectionField
      label={t(`catalogue.fields.${labelKey}`)}
      hint={hint}
      options={source.options}
      groups={source.groups}
      selected={values[field] as string[]}
      onChange={(next) => set(field, next)}
      error={errors[field]?.[0]}
    />
  );

  return (
    <>
      <Sheet title={title} onClose={onClose} side="right" className="w-[36rem] max-w-full">
        <form
          className="flex min-h-0 flex-1 flex-col"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
            {(["name_fr", "name_en"] as const).map((field) => (
              <Field
                key={field}
                id={`catalogue-${field}`}
                label={t(`catalogue.fields.${field}`)}
                error={errors[field]}
                required
              >
                <textarea
                  id={`catalogue-${field}`}
                  rows={2}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={values[field] as string}
                  onChange={(event) => set(field, event.target.value)}
                />
              </Field>
            ))}

            {needs("css_class") && (
              <Field label={t("catalogue.fields.css_class")} error={errors.css_class} required>
                <div role="radiogroup" className="flex flex-wrap gap-2">
                  {THEMATIC_KEYS.map((key) => {
                    const theme = thematicClasses(key);
                    const chosen = values.css_class === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        role="radio"
                        aria-checked={chosen}
                        onClick={() => set("css_class", key)}
                        className={cn(
                          "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm",
                          chosen ? cn("ring-2", theme.ring, theme.border) : "border-border",
                        )}
                      >
                        <span aria-hidden className={cn("size-3 rounded-full", theme.solid)} />
                        {t(`catalogue.colours.${key}`)}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    role="radio"
                    aria-checked={values.css_class === CUSTOM}
                    onClick={() => set("css_class", CUSTOM)}
                    className={cn(
                      "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm",
                      values.css_class === CUSTOM ? "border-ring ring-2 ring-ring" : "border-border",
                    )}
                  >
                    {/* A hue ring for the wheel it opens, as on the Django form. */}
                    <span
                      aria-hidden
                      className="size-3 rounded-full bg-[conic-gradient(#e03131,#f59f00,#2f9e44,#1c7ed6,#9c36b5,#e03131)]"
                    />
                    {t("catalogue.colours.custom")}
                  </button>
                </div>
              </Field>
            )}
            {needs("colour") && values.css_class === CUSTOM && (
              <Field
                id="catalogue-colour"
                label={t("catalogue.fields.colour")}
                error={errors.colour}
                required
              >
                <ColourPicker
                  id="catalogue-colour"
                  value={values.colour as string}
                  onChange={(next) => set("colour", next)}
                  invalid={Boolean(errors.colour)}
                />
              </Field>
            )}
            {(["experts_enabled", "tools_enabled", "financial_aids_enabled"] as const)
              .filter(needs)
              .map((field) => (
                <div key={field} className="space-y-1">
                  <div className="flex items-center justify-between gap-3">
                    <Label>{t(`catalogue.fields.${field}`)}</Label>
                    <Switch
                      checked={values[field] as boolean}
                      onCheckedChange={(next) => set(field, next)}
                      label={t(`catalogue.fields.${field}`)}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">{t(`catalogue.help.${field}`)}</p>
                </div>
              ))}

            {needs("thematic") &&
              single(
                "thematic",
                (thematics.data ?? []).map((row) => ({ id: row.id, name: row.name })),
              )}
            {needs("issue") &&
              single(
                "issue",
                (issues.data ?? []).map((row) => ({
                  id: row.id,
                  name: `${row.crumb} › ${row.name}`,
                })),
              )}
            {needs("goal") &&
              single(
                "goal",
                (goals.data ?? []).map((row) => ({
                  id: row.id,
                  name: `${names.get(row.parent_id ?? "") ?? ""} › ${row.name}`,
                })),
              )}
            {needs("category") &&
              single(
                "category",
                (categories.data ?? []).map((row) => ({ id: row.id, name: row.name })),
              )}
            {needs("group") &&
              single(
                "group",
                (groups.data ?? []).map((row) => ({ id: row.id, name: row.name })),
                true,
              )}
            {needs("prerequisite") &&
              single(
                "prerequisite",
                (characteristics.data ?? [])
                  .filter((row) => row.id !== item?.id)
                  .map((row) => ({ id: row.id, name: row.name })),
                true,
              )}
            {needs("icon") && (
              <Field
                id="catalogue-icon"
                label={t("catalogue.fields.icon")}
                error={errors.icon}
                hint={
                  <>
                    {t("catalogue.icon.hint")}{" "}
                    <a
                      href={FONT_AWESOME_GALLERY}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary underline-offset-4 hover:underline"
                    >
                      {t("catalogue.icon.gallery")}
                    </a>
                  </>
                }
              >
                <div className="flex items-center gap-2">
                  <IconPreview icon={values.icon as string} />
                  <Input
                    id="catalogue-icon"
                    maxLength={30}
                    value={values.icon as string}
                    onChange={(event) => set("icon", event.target.value)}
                  />
                  <Button type="button" variant="outline" onClick={() => setPickingIcon(true)}>
                    {t("catalogue.icon.choose")}
                  </Button>
                </div>
              </Field>
            )}

            {kind === "issue" &&
              many("characteristics", "triggering_characteristics", characteristicOptions)}
            {kind === "action" &&
              many("sectors", "sectors", sectorOptions, t("catalogue.emptyMeansAll"))}
            {kind === "action" &&
              many(
                "characteristics",
                "characteristics",
                characteristicOptions,
                t("catalogue.emptyMeansAll"),
              )}
            {kind === "action" &&
              many(
                "company_sizes",
                "company_sizes",
                { options: sizeOptions },
                t("catalogue.emptyMeansAll"),
              )}
            {kind === "sector" && many("unavoidable_issues", "unavoidable_issues", issueOptions)}
            {kind === "sector" &&
              many("characteristics", "offered_characteristics", characteristicOptions)}
            {kind === "characteristic" && many("sectors", "sectors", sectorOptions)}
            {kind === "characteristic" && many("issues", "issues", issueOptions)}
            {(kind === "sector" || kind === "characteristic") &&
              many("actions", "actions", actionOptions)}

            {failure && (
              <p role="alert" className="text-sm text-destructive-ink">
                {failure}
              </p>
            )}
          </div>
          <div className="flex justify-end gap-2 border-t border-border p-4">
            <Button type="button" variant="outline" onClick={onClose}>
              {t("catalogue.cancel")}
            </Button>
            <Button type="submit" disabled={flow.isPending}>
              {t("catalogue.save")}
            </Button>
          </div>
        </form>
      </Sheet>
      {pickingIcon && (
        <IconPicker
          current={values.icon as string}
          onPick={(icon) => {
            set("icon", icon);
            setPickingIcon(false);
          }}
          onClose={() => setPickingIcon(false)}
        />
      )}
      {flow.confirming && (
        <ImpactDialog
          lines={flow.confirming.lines}
          stale={flow.confirming.stale}
          isPending={flow.isPending}
          onConfirm={() => void confirm()}
          onCancel={flow.dismiss}
        />
      )}
    </>
  );
}
